import React, { useEffect, useState, useRef } from 'react';

export default function CyberMouseFollower() {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  // Smooth lerp positions using refs to prevent React re-render lags
  const mouseRef = useRef({ x: -100, y: -100 });
  const ringRef = useRef({ x: -100, y: -100 });
  const blobRef = useRef({ x: -100, y: -100 });
  const dotRef = useRef({ x: -100, y: -100 });

  const ringElemRef = useRef(null);
  const blobElemRef = useRef(null);
  const dotElemRef = useRef(null);

  useEffect(() => {
    // Check if pointer device supports hover (desktop vs mobile)
    const isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (isTouchDevice) return;

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseDown = () => setIsClicked(true);
    const handleMouseUp = () => setIsClicked(false);

    const handleMouseLeave = () => setIsVisible(false);
    const handleMouseEnter = () => setIsVisible(true);

    // Delegate hover detection on clickable interactive elements
    const handleMouseOver = (e) => {
      const target = e.target;
      if (!target) return;
      const isInteractive = target.closest(
        'button, a, input, select, textarea, [role="button"], .cursor-pointer, .cyber-btn, [data-hover="true"]'
      );
      setIsHovered(!!isInteractive);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    // Physics Animation Loop using requestAnimationFrame
    let animId;
    const animate = () => {
      const target = mouseRef.current;

      // Lerp ring position (smooth medium follow)
      ringRef.current.x += (target.x - ringRef.current.x) * 0.16;
      ringRef.current.y += (target.y - ringRef.current.y) * 0.16;

      // Lerp dot position (tight fast follow)
      dotRef.current.x += (target.x - dotRef.current.x) * 0.35;
      dotRef.current.y += (target.y - dotRef.current.y) * 0.35;

      // Lerp blob position (soft trailing lag)
      blobRef.current.x += (target.x - blobRef.current.x) * 0.08;
      blobRef.current.y += (target.y - blobRef.current.y) * 0.08;

      // Direct DOM updates for 60-120fps performance without React state thrashing
      if (ringElemRef.current) {
        ringElemRef.current.style.transform = `translate3d(${ringRef.current.x}px, ${ringRef.current.y}px, 0) translate(-50%, -50%)`;
      }
      if (dotElemRef.current) {
        dotElemRef.current.style.transform = `translate3d(${dotRef.current.x}px, ${dotRef.current.y}px, 0) translate(-50%, -50%)`;
      }
      if (blobElemRef.current) {
        blobElemRef.current.style.transform = `translate3d(${blobRef.current.x}px, ${blobRef.current.y}px, 0) translate(-50%, -50%)`;
      }

      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      cancelAnimationFrame(animId);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[99999] overflow-hidden select-none">
      <div
        ref={blobElemRef}
        className={`absolute rounded-full bg-gradient-to-r from-[#00f0ff]/20 via-[#bd00ff]/15 to-[#ff007a]/15 blur-[10px] transition-all duration-300 ease-out will-change-transform ${
          isHovered
            ? 'w-16 h-16 opacity-40 blur-[14px]'
            : 'w-12 h-12 opacity-60'
        } ${isClicked ? 'scale-90' : ''}`}
      />

      <div
        ref={ringElemRef}
        className={`absolute rounded-full border bg-transparent transition-all duration-300 ease-out will-change-transform flex items-center justify-center ${
          isHovered
            ? 'w-10 h-10 border-[#00f0ff] shadow-[0_0_18px_rgba(0,240,255,0.7)]'
            : 'w-6 h-6 border-[#00f0ff]/80 shadow-[0_0_10px_rgba(0,240,255,0.4)]'
        } ${isClicked ? 'scale-75 border-white shadow-[0_0_14px_#ffffff]' : ''}`}
      />

      <div
        ref={dotElemRef}
        className={`absolute rounded-full bg-[#00f0ff] shadow-[0_0_6px_#00f0ff] transition-all duration-200 ease-out will-change-transform ${
          isHovered
            ? 'w-1 h-1 opacity-50 bg-[#00f0ff]'
            : 'w-1.5 h-1.5 opacity-90'
        } ${isClicked ? 'scale-150 bg-white' : ''}`}
      />
    </div>
  );
}
